import { useState } from "react";
import { Aviso, BarraLimite, Panel } from "../componentes/piezas";
import { creditos, gramos, iniciales } from "../lib/formato";

/**
 * El mostrador de la web pública: el de verdad, con datos inventados.
 *
 * Una web de producto tiene un titular, una pantalla al lado y filas que
 * alternan de lado. Se reconoce a la primera, porque es la misma en todas. Lo
 * que no tiene ninguna es un mostrador que funcione: eso solo lo puede poner
 * quien tiene el programa. Así que en vez de describir el mostrador, se deja
 * usar.
 *
 * Son las mismas piezas y las mismas clases que el mostrador real —Panel,
 * BarraLimite, tarjeta-producto, carrito-linea— para que lo que se toca aquí
 * sea lo que hay dentro. Y las reglas son las que aplica la API, copiadas en
 * pequeño: el límite diario y mensual del socio, la cartera que no cubre, el
 * socio con la cuota caducada, y la autorización cuando el pedido se pasa.
 *
 * Lo que no hace es hablar con el servidor: nada de esto se guarda, y al
 * recargar la página vuelve a empezar. Los socios no existen.
 *
 * Todo va en céntimos y en miligramos, como en el programa: la regla de la
 * casa es no sumar decimales, y el mostrador de muestra no es una excepción.
 */

interface SocioDemo {
  id: string;
  nombre: string;
  numero: number;
  tipo: string;
  /** Rebaja del tipo de socio sobre el catálogo, en tanto por ciento. */
  descuento: number;
  limiteDiaMg: number;
  limiteMesMg: number;
  hoyMg: number;
  mesMg: number;
  saldoCentimos: number;
  hasta: string;
  /** El motivo por el que no se le puede dispensar, con las palabras de la API. */
  bloqueo: string | null;
}

interface ProductoDemo {
  id: string;
  nombre: string;
  categoria: string;
  color: string;
  /** Precio de catálogo por gramo. */
  precioCentimos: number;
  thc?: number;
}

const SOCIOS: SocioDemo[] = [
  {
    id: "marta",
    nombre: "Marta Ruiz",
    numero: 118,
    tipo: "Socio estándar",
    descuento: 0,
    limiteDiaMg: 10_000,
    limiteMesMg: 150_000,
    hoyMg: 3_500,
    mesMg: 42_000,
    saldoCentimos: 3_850,
    hasta: "03/2027",
    bloqueo: null,
  },
  {
    id: "jordi",
    nombre: "Jordi Llop",
    numero: 42,
    tipo: "Socio terapéutico",
    descuento: 10,
    limiteDiaMg: 10_000,
    limiteMesMg: 150_000,
    hoyMg: 9_000,
    mesMg: 121_000,
    saldoCentimos: 2_000,
    hasta: "11/2026",
    bloqueo: null,
  },
  {
    id: "nuria",
    nombre: "Nuria Pons",
    numero: 7,
    tipo: "Fundadora",
    descuento: 15,
    limiteDiaMg: 15_000,
    limiteMesMg: 200_000,
    hoyMg: 0,
    mesMg: 18_000,
    saldoCentimos: 0,
    hasta: "01/2028",
    bloqueo: null,
  },
  {
    id: "pau",
    nombre: "Pau Serra",
    numero: 203,
    tipo: "Socio estándar",
    descuento: 0,
    limiteDiaMg: 10_000,
    limiteMesMg: 150_000,
    hoyMg: 0,
    mesMg: 0,
    saldoCentimos: 500,
    hasta: "09/2026",
    bloqueo: "La cuota del socio ha caducado: hay que renovarla",
  },
];

const PRODUCTOS: ProductoDemo[] = [
  {
    id: "amnesia",
    nombre: "Amnesia Haze",
    categoria: "Flor",
    color: "#4f8a5b",
    precioCentimos: 800,
    thc: 19,
  },
  {
    id: "critical",
    nombre: "Critical",
    categoria: "Flor",
    color: "#4f8a5b",
    precioCentimos: 700,
    thc: 17,
  },
  {
    id: "gelato",
    nombre: "Gelato",
    categoria: "Flor",
    color: "#4f8a5b",
    precioCentimos: 950,
    thc: 22,
  },
  {
    id: "ketama",
    nombre: "Hash Ketama",
    categoria: "Hash",
    color: "#8a6d3b",
    precioCentimos: 600,
  },
  {
    id: "ice",
    nombre: "Ice-O-Lator",
    categoria: "Hash",
    color: "#8a6d3b",
    precioCentimos: 1_400,
  },
  {
    id: "rosin",
    nombre: "Rosin",
    categoria: "Extracto",
    color: "#6b5b95",
    precioCentimos: 3_000,
  },
];

/** Las cantidades de un toque, las mismas que en el mostrador. */
const CANTIDADES_RAPIDAS = [0.5, 1, 2, 3, 5, 10];

/** El número de la primera entrega que se registre: uno que parezca de un club con vida. */
const PRIMERA_ENTREGA = 1_284;

interface Linea {
  producto: ProductoDemo;
  gramos: number;
}

interface Entrega {
  numero: number;
  lineas: { nombre: string; mg: number; importeCentimos: number }[];
  totalCentimos: number;
  ahorroCentimos: number;
  metodoPago: string;
  saldoRestanteCentimos: number | null;
}

const ETIQUETA_PAGO: Record<string, string> = {
  EFECTIVO: "Efectivo",
  CARTERA: "Cartera",
  TARJETA: "Tarjeta",
};

export default function MostradorDemo() {
  const [socios, setSocios] = useState(SOCIOS);
  const [socioId, setSocioId] = useState(SOCIOS[0]!.id);
  const [carrito, setCarrito] = useState<Linea[]>([]);
  const [metodoPago, setMetodoPago] = useState("CARTERA");
  const [pidiendoAutorizacion, setPidiendoAutorizacion] = useState(false);
  const [entrega, setEntrega] = useState<Entrega | null>(null);
  const [siguienteNumero, setSiguienteNumero] = useState(PRIMERA_ENTREGA);

  const socio = socios.find((s) => s.id === socioId)!;

  /* El precio que paga este socio: el de catálogo menos la rebaja de su tipo. */
  const precioDe = (p: ProductoDemo) =>
    Math.round((p.precioCentimos * (100 - socio.descuento)) / 100);
  const importeLinea = (l: Linea) =>
    Math.round((precioDe(l.producto) * Math.round(l.gramos * 1000)) / 1000);
  const importeBase = (l: Linea) =>
    Math.round(
      (l.producto.precioCentimos * Math.round(l.gramos * 1000)) / 1000,
    );

  const totalCentimos = carrito.reduce((s, l) => s + importeLinea(l), 0);
  const ahorroCentimos =
    carrito.reduce((s, l) => s + importeBase(l), 0) - totalCentimos;
  const totalMg = carrito.reduce((s, l) => s + Math.round(l.gramos * 1000), 0);

  const disponibleMg = Math.max(
    0,
    Math.min(socio.limiteDiaMg - socio.hoyMg, socio.limiteMesMg - socio.mesMg),
  );
  const pasaLimite = totalMg > disponibleMg;
  const saldoInsuficiente =
    metodoPago === "CARTERA" && socio.saldoCentimos < totalCentimos;

  const ultimaLinea = carrito[carrito.length - 1];

  function elegir(id: string) {
    const elegido = socios.find((s) => s.id === id)!;
    setSocioId(id);
    setCarrito([]);
    setEntrega(null);
    setPidiendoAutorizacion(false);
    // Como en el programa: si hay cartera, se cobra de la cartera.
    setMetodoPago(elegido.saldoCentimos > 0 ? "CARTERA" : "EFECTIVO");
  }

  function anadir(producto: ProductoDemo) {
    setEntrega(null);
    setPidiendoAutorizacion(false);
    setCarrito((antes) => {
      const existe = antes.find((l) => l.producto.id === producto.id);
      if (existe)
        return antes.map((l) =>
          l === existe ? { ...l, gramos: l.gramos + 1 } : l,
        );
      return [...antes, { producto, gramos: 1 }];
    });
  }

  function cambiarCantidad(id: string, gramosNuevos: number) {
    setPidiendoAutorizacion(false);
    if (!(gramosNuevos > 0)) return;
    setCarrito((antes) =>
      antes.map((l) =>
        l.producto.id === id ? { ...l, gramos: gramosNuevos } : l,
      ),
    );
  }

  function quitar(id: string) {
    setPidiendoAutorizacion(false);
    setCarrito((antes) => antes.filter((l) => l.producto.id !== id));
  }

  function registrar(autorizado: boolean) {
    if (pasaLimite && !autorizado) {
      setPidiendoAutorizacion(true);
      return;
    }
    const numero = siguienteNumero;
    setSiguienteNumero(numero + 1);
    setEntrega({
      numero,
      lineas: carrito.map((l) => ({
        nombre: l.producto.nombre,
        mg: Math.round(l.gramos * 1000),
        importeCentimos: importeLinea(l),
      })),
      totalCentimos,
      ahorroCentimos,
      metodoPago,
      saldoRestanteCentimos:
        metodoPago === "CARTERA" ? socio.saldoCentimos - totalCentimos : null,
    });
    // Lo que se acaba de entregar cuenta desde ya para el límite, y la
    // cartera baja: la siguiente entrega del mismo socio lo nota.
    setSocios((antes) =>
      antes.map((s) =>
        s.id !== socio.id
          ? s
          : {
              ...s,
              hoyMg: s.hoyMg + totalMg,
              mesMg: s.mesMg + totalMg,
              saldoCentimos:
                metodoPago === "CARTERA"
                  ? s.saldoCentimos - totalCentimos
                  : s.saldoCentimos,
            },
      ),
    );
    setCarrito([]);
    setPidiendoAutorizacion(false);
  }

  return (
    <div className="demo">
      <aside className="demo-lateral" aria-hidden="true">
        <div className="demo-marca">
          <span>C</span>
          <div>
            <strong>Club de la Sierra</strong>
            <small>Gestión de la asociación</small>
          </div>
        </div>
        <div className="demo-enlace">Inicio</div>
        <div className="demo-enlace activo">Mostrador</div>
        <div className="demo-grupo">Asociación</div>
        <div className="demo-enlace">Donaciones</div>
        <div className="demo-enlace">Socios</div>
        <div className="demo-enlace">Caja</div>
        <div className="demo-enlace">Gastos y compras</div>
        <div className="demo-enlace">Inventario</div>
        <div className="demo-enlace">Recuentos</div>
        <div className="demo-enlace">Barra</div>
        <div className="demo-enlace">Dispensaciones</div>
        <div className="demo-grupo">Ajustes</div>
        <div className="demo-enlace">Informes</div>
        <div className="demo-enlace">Ajustes</div>
        <div className="demo-enlace">Cuotas</div>
      </aside>

      <div className="demo-contenido">
        <div className="demo-cabecera">
          <h2>Mostrador</h2>
          <span className="suave pequeno">
            Datos inventados. Nada se guarda.
          </span>
        </div>

        <div className="demo-mostrador">
          <Panel titulo="Productos">
            <div className="rejilla-productos">
              {PRODUCTOS.map((producto) => (
                <button
                  key={producto.id}
                  type="button"
                  className="tarjeta-producto"
                  disabled={Boolean(socio.bloqueo)}
                  onClick={() => anadir(producto)}
                >
                  <div
                    className="cinta"
                    style={{ background: producto.color }}
                  />
                  <span className="nombre">{producto.nombre}</span>
                  <span className="suave pequeno">
                    {producto.thc ? `THC ${producto.thc}%` : producto.categoria}
                  </span>
                  {socio.descuento > 0 && (
                    <span
                      className="etiqueta verde"
                      style={{ alignSelf: "flex-start" }}
                    >
                      −{socio.descuento}%
                    </span>
                  )}
                  <div className="fila separada" style={{ marginTop: 4 }}>
                    <strong className="tabular">
                      {socio.descuento > 0 && (
                        <span
                          className="suave"
                          style={{
                            textDecoration: "line-through",
                            fontWeight: 400,
                            marginRight: 4,
                          }}
                        >
                          {creditos(producto.precioCentimos)}
                        </span>
                      )}
                      {creditos(precioDe(producto))}/g
                    </strong>
                  </div>
                </button>
              ))}
            </div>
          </Panel>

          <div className="columna" style={{ gap: 16 }}>
            <Panel titulo="Socio">
              <div
                className="demo-socios"
                role="group"
                aria-label="Elige un socio"
              >
                {socios.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`boton ${s.id === socioId ? "" : "secundario"}`}
                    onClick={() => elegir(s.id)}
                  >
                    {s.nombre}
                  </button>
                ))}
              </div>

              <div className="fila" style={{ gap: 10, marginTop: 14 }}>
                <div className="avatar">{iniciales(socio.nombre)}</div>
                <div>
                  <strong>{socio.nombre}</strong>
                  <div className="suave pequeno">
                    #{socio.numero} · {socio.tipo} · hasta {socio.hasta}
                  </div>
                </div>
              </div>

              {socio.bloqueo && (
                <div style={{ marginTop: 12 }}>
                  <Aviso tipo="error">{socio.bloqueo}</Aviso>
                </div>
              )}

              <div className="columna" style={{ gap: 12, marginTop: 14 }}>
                <BarraLimite
                  consumidoMg={socio.hoyMg}
                  limiteMg={socio.limiteDiaMg}
                  etiqueta="Hoy"
                />
                <BarraLimite
                  consumidoMg={socio.mesMg}
                  limiteMg={socio.limiteMesMg}
                  etiqueta="Este mes"
                />
              </div>

              <div className="fila separada" style={{ marginTop: 12 }}>
                <span className="suave pequeno">Cartera</span>
                <strong className="tabular">
                  {creditos(socio.saldoCentimos)}
                </strong>
              </div>
            </Panel>

            <Panel titulo="Entrega">
              {entrega ? (
                <>
                  <h3 style={{ marginBottom: 6 }}>
                    Entrega #{entrega.numero} registrada
                  </h3>
                  <div className="resumen-venta">
                    {entrega.lineas.map((l, i) => (
                      <div key={i} className="resumen-venta-linea">
                        <span>
                          {l.nombre} · {gramos(l.mg)}
                        </span>
                        <span className="tabular">
                          {creditos(l.importeCentimos)}
                        </span>
                      </div>
                    ))}
                    <div className="resumen-venta-total">
                      <span>Total</span>
                      <strong className="tabular">
                        {creditos(entrega.totalCentimos)}
                      </strong>
                    </div>
                  </div>
                  <div className="suave pequeno">
                    {gramos(entrega.lineas.reduce((s, l) => s + l.mg, 0))} ·{" "}
                    {ETIQUETA_PAGO[entrega.metodoPago]}
                    {entrega.ahorroCentimos > 0 && (
                      <> · Ahorro {creditos(entrega.ahorroCentimos)}</>
                    )}
                    {entrega.saldoRestanteCentimos !== null && (
                      <>
                        {" "}
                        · Quedan {creditos(entrega.saldoRestanteCentimos)} en la
                        cartera
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    className="boton bloque"
                    style={{ marginTop: 14 }}
                    onClick={() => setEntrega(null)}
                  >
                    Otra entrega
                  </button>
                </>
              ) : carrito.length === 0 ? (
                <div className="vacio">Toca un producto para añadirlo.</div>
              ) : (
                <>
                  {carrito.map((linea) => (
                    <div key={linea.producto.id} className="carrito-linea">
                      <div>
                        <strong>{linea.producto.nombre}</strong>
                        <div className="suave pequeno tabular">
                          {creditos(precioDe(linea.producto))}/g ·{" "}
                          {creditos(importeLinea(linea))}
                        </div>
                      </div>
                      <div className="fila">
                        <input
                          type="number"
                          min={0.1}
                          step={0.1}
                          value={linea.gramos}
                          aria-label={`Gramos de ${linea.producto.nombre}`}
                          onChange={(e) =>
                            cambiarCantidad(
                              linea.producto.id,
                              Number(e.target.value),
                            )
                          }
                        />
                        <span className="suave pequeno">g</span>
                        <button
                          type="button"
                          className="boton fantasma"
                          onClick={() => quitar(linea.producto.id)}
                          aria-label={`Quitar ${linea.producto.nombre}`}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="teclado" style={{ marginTop: 12 }}>
                    {CANTIDADES_RAPIDAS.map((cantidad) => (
                      <button
                        key={cantidad}
                        type="button"
                        onClick={() => {
                          if (ultimaLinea)
                            cambiarCantidad(ultimaLinea.producto.id, cantidad);
                        }}
                      >
                        {cantidad} g
                      </button>
                    ))}
                  </div>

                  <div
                    className="fila separada"
                    style={{
                      marginTop: 16,
                      paddingTop: 12,
                      borderTop: "1px solid var(--borde)",
                    }}
                  >
                    <span className="suave">{gramos(totalMg)}</span>
                    <strong className="tabular" style={{ fontSize: "1.25rem" }}>
                      {creditos(totalCentimos)}
                    </strong>
                  </div>
                  {ahorroCentimos > 0 && (
                    <div
                      className="suave pequeno"
                      style={{ textAlign: "right" }}
                    >
                      Ahorro {creditos(ahorroCentimos)} · {socio.tipo}
                    </div>
                  )}

                  {pasaLimite && (
                    <div style={{ marginTop: 12 }}>
                      <Aviso tipo="atencion">
                        Supera el límite disponible ({gramos(disponibleMg)}).
                        {pidiendoAutorizacion ? (
                          <>
                            {" "}
                            Si se registra igual, queda apuntado quién lo
                            autorizó.
                            <div
                              className="fila"
                              style={{
                                gap: 8,
                                marginTop: 10,
                                justifyContent: "flex-end",
                              }}
                            >
                              <button
                                type="button"
                                className="boton secundario"
                                onClick={() => setPidiendoAutorizacion(false)}
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                className="boton"
                                onClick={() => registrar(true)}
                              >
                                Autorizar y registrar
                              </button>
                            </div>
                          </>
                        ) : (
                          " Para registrarla hay que autorizarlo."
                        )}
                      </Aviso>
                    </div>
                  )}

                  {saldoInsuficiente && (
                    <div style={{ marginTop: 12 }}>
                      <Aviso tipo="error">
                        La cartera del socio no tiene saldo suficiente.
                      </Aviso>
                    </div>
                  )}

                  <div className="fila" style={{ marginTop: 12, gap: 6 }}>
                    {["EFECTIVO", "CARTERA", "TARJETA"].map((forma) => (
                      <button
                        key={forma}
                        type="button"
                        className={`boton ${metodoPago === forma ? "" : "secundario"}`}
                        style={{ flex: 1 }}
                        onClick={() => {
                          setMetodoPago(forma);
                          setPidiendoAutorizacion(false);
                        }}
                      >
                        {ETIQUETA_PAGO[forma]}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="boton grande bloque"
                    style={{ marginTop: 12 }}
                    disabled={
                      Boolean(socio.bloqueo) ||
                      saldoInsuficiente ||
                      pidiendoAutorizacion
                    }
                    onClick={() => registrar(false)}
                  >
                    Registrar entrega · {creditos(totalCentimos)}
                  </button>
                </>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
